import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import Dialog from '@mui/material/Dialog';
import Icon, { IconSizes, IconTypes } from '../../../components/foundations/Icon';
import Button from '../../../components/atoms/Button';
import { FEEDBACK_COMMENT_MAX_LENGTH, NEGATIVE_REASON_KEYS } from '../../constants';
import FeedbackChips from './FeedbackChips';
import css from './FeedbackModal.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.FEEDBACK';

const FeedbackModal = ({
    open,
    selectedReasonKeys,
    comment,
    onChipToggle,
    onCommentChange,
    onCancel,
    onSubmit,
    translate,
}) => {
    const textareaRef = useRef(null);
    const canSubmit = selectedReasonKeys.length > 0 || comment.trim().length > 0;

    useEffect(() => {
        if (!open) return undefined;
        const focusFrameId = window.requestAnimationFrame(() => textareaRef.current?.focus());
        return () => window.cancelAnimationFrame(focusFrameId);
    }, [open]);

    const handleKeyDown = event => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSubmit) {
            event.preventDefault();
            onSubmit();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            BackdropProps={{ className: css.backdrop }}
            PaperProps={{ className: css.paper, onKeyDown: handleKeyDown }}
        >
            <div className={css.header}>
                <h2 className={css.title}>
                    <Translate id={`${TRANSLATION_PREFIX}.HEADER`} />
                </h2>
                <Icon
                    name="x"
                    type={IconTypes.TABLER}
                    size={IconSizes.SMALL}
                    className={css.closeButton}
                    onClick={onCancel}
                />
            </div>
            <div className={css.body}>
                <div className={css.bodyHeader}>
                    <Translate id={`${TRANSLATION_PREFIX}.NEGATIVE_HEADER`} />
                </div>
                <div className={css.chipsWrapper}>
                    <FeedbackChips
                        reasonKeys={NEGATIVE_REASON_KEYS}
                        selectedReasonKeys={selectedReasonKeys}
                        translationPrefix={`${TRANSLATION_PREFIX}.NEGATIVE_REASONS`}
                        onToggle={onChipToggle}
                    />
                </div>
                <textarea
                    ref={textareaRef}
                    className={css.comment}
                    value={comment}
                    maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
                    onChange={event => onCommentChange(event.target.value)}
                    placeholder={translate(`${TRANSLATION_PREFIX}.COMMENT_PLACEHOLDER`)}
                />
                <div className={css.infoBanner}>
                    <Icon
                        name="info-circle"
                        type={IconTypes.TABLER}
                        size={IconSizes.X_SMALL}
                        className={css.infoBannerIcon}
                        interactive={false}
                    />
                    <span className={css.infoBannerText}>
                        <Translate id={`${TRANSLATION_PREFIX}.INFO_BANNER`} />
                    </span>
                </div>
            </div>
            <div className={css.footer}>
                <Button type="flat" onClick={onCancel}>
                    <Translate id={`${TRANSLATION_PREFIX}.CANCEL`} />
                </Button>
                <Button onClick={onSubmit} disabled={!canSubmit}>
                    <Translate id={`${TRANSLATION_PREFIX}.SUBMIT`} />
                </Button>
            </div>
        </Dialog>
    );
};

FeedbackModal.propTypes = {
    open: PropTypes.bool.isRequired,
    selectedReasonKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    comment: PropTypes.string.isRequired,
    onChipToggle: PropTypes.func.isRequired,
    onCommentChange: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(FeedbackModal);
